/**
 * Keep the panel inside the on-screen visual viewport.
 *
 * iOS WKWebView often pans (instead of shrinking) when the keyboard opens.
 * `window.innerHeight` can shrink too, so we cannot detect the keyboard by
 * comparing innerHeight vs visualViewport.height. We snapshot the idle
 * height and treat a drop of >80px (or a visualViewport pan) as keyboard-open.
 *
 * Do not use input focus alone — during send the field can stay focused after
 * the keypad dismisses, which used to zero safe-area and drop the composer.
 *
 * Android edge-to-edge WebView does not reliably shrink visualViewport.
 * The native SDK pushes IME height into `--avatar-ime-bottom` instead.
 */
export function bindVisualViewport(): () => void {
  const root = document.documentElement;
  const isAndroid = /Android/i.test(navigator.userAgent);
  let idleHeight = readViewportHeight();

  const isTypingField = (el: EventTarget | null): boolean =>
    el instanceof HTMLElement &&
    (el.tagName === 'TEXTAREA' || el.tagName === 'INPUT');

  const restoreSafeBottom = () =>
    'max(env(safe-area-inset-bottom, 0px), var(--avatar-safe-bottom, 0px))';

  const sync = () => {
    const focused = isTypingField(document.activeElement);

    // Android: the native SDK resizes nothing. It reports IME height as
    // --avatar-ime-bottom and the composer pads by that. Never shrink
    // html to visualViewport — that plus IME padding leaves a gap above
    // the keypad (and a tiny scroll region at the top).
    if (isAndroid) {
      const ime =
        parseFloat(
          getComputedStyle(root).getPropertyValue('--avatar-ime-bottom'),
        ) || 0;
      const keyboardOpen = ime > 40;
      root.style.setProperty('--avatar-vv-height', '100%');
      root.style.setProperty('--avatar-vv-offset', '0px');
      root.classList.toggle('keyboard-open', keyboardOpen);
      root.style.setProperty(
        '--avatar-kb-safe-bottom',
        keyboardOpen ? '0px' : restoreSafeBottom(),
      );
      if (keyboardOpen && focused) window.scrollTo(0, 0);
      return;
    }

    const height = readViewportHeight();
    const offset = window.visualViewport?.offsetTop ?? 0;
    const heightDropped = idleHeight - height > 80;
    const panned = offset > 40;
    const keyboardOpen = heightDropped || panned;

    if (!keyboardOpen) {
      idleHeight = height;
    }

    root.style.setProperty(
      '--avatar-vv-height',
      heightDropped ? `${Math.round(height)}px` : '100%',
    );
    root.style.setProperty('--avatar-vv-offset', `${Math.round(offset)}px`);
    root.classList.toggle('keyboard-open', keyboardOpen);
    root.style.setProperty(
      '--avatar-kb-safe-bottom',
      keyboardOpen ? '0px' : restoreSafeBottom(),
    );

    if (keyboardOpen) {
      window.scrollTo(0, 0);
    }
  };

  sync();
  window.visualViewport?.addEventListener('resize', sync);
  window.visualViewport?.addEventListener('scroll', sync);
  window.addEventListener('focusin', sync);
  window.addEventListener('focusout', sync);
  window.addEventListener('orientationchange', () => {
    window.setTimeout(() => {
      idleHeight = readViewportHeight();
      sync();
    }, 300);
  });

  return () => {
    window.visualViewport?.removeEventListener('resize', sync);
    window.visualViewport?.removeEventListener('scroll', sync);
    window.removeEventListener('focusin', sync);
    window.removeEventListener('focusout', sync);
  };
}

function readViewportHeight(): number {
  return window.visualViewport?.height ?? window.innerHeight;
}
